import { useMemo } from 'react'
import { differenceInDays, differenceInHours, differenceInMinutes, isAfter } from 'date-fns'
import { type Address, useBotData, useReadAppExpiration } from 'use-towns-client'

const ONE_MONTH_IN_SECONDS = 31 * 24 * 60 * 60 // 31 days

export function useAppRenewCountdownData(args: {
    spaceId: string | undefined
    appAddress: Address | undefined
}) {
    const { spaceId, appAddress } = args
    const { data: expiration, isLoading: isLoadingExpiration } = useReadAppExpiration(
        spaceId,
        appAddress,
    )
    const { data: botData, isLoading: isLoadingBot } = useBotData(appAddress)

    return useMemo(() => {
        const isLoading = isLoadingExpiration || isLoadingBot

        if (!expiration || expiration === 0) {
            return {
                isLoading,
                isExpired: false,
                isExpiringSoon: false,
                expiryTime: 0,
                timeRemaining: '0 days',
                timeRemainingSeconds: 0,
                daysRemaining: 0,
                shouldDisplay: false,
                displayWindow: 30,
                accessDuration: botData?.accessDuration,
            }
        }

        const now = Math.floor(Date.now() / 1000)
        const nowDate = new Date()
        const expiryTime = Number(expiration)
        const expirationDate = new Date(expiryTime * 1000)
        const isExpired = isAfter(nowDate, expirationDate)
        const timeRemainingSeconds = Math.max(0, expiryTime - now)
        const daysRemaining = isExpired
            ? 0
            : Math.max(0, Math.ceil(timeRemainingSeconds / (24 * 60 * 60)))

        // Format time remaining based on how much time is left
        let timeRemainingFormatted: string
        const days = differenceInDays(expirationDate, nowDate)

        if (days >= 1) {
            timeRemainingFormatted = `${days} day${days !== 1 ? 's' : ''}`
        } else {
            const hours = differenceInHours(expirationDate, nowDate)
            if (hours >= 1) {
                timeRemainingFormatted = `${hours} hour${hours !== 1 ? 's' : ''}`
            } else {
                const minutes = Math.max(0, differenceInMinutes(expirationDate, nowDate))
                timeRemainingFormatted = `${minutes} minute${minutes !== 1 ? 's' : ''}`
            }
        }

        // Determine display window based on subscription duration
        // If subscription is less than a month: show only in last 7 days
        // If yearly subscription: show in last 30 days
        const accessDurationSeconds = botData?.accessDuration
            ? Number(botData.accessDuration)
            : undefined
        let displayWindow = 30

        if (accessDurationSeconds && accessDurationSeconds < ONE_MONTH_IN_SECONDS) {
            displayWindow = 7
        }

        // Calculate if expiring soon (80% of time has passed)
        let isExpiringSoon = false
        if (accessDurationSeconds) {
            const installationStartTime = expiryTime - accessDurationSeconds
            const timeElapsed = now - installationStartTime
            const percentageElapsed = timeElapsed / accessDurationSeconds
            isExpiringSoon = percentageElapsed >= 0.8 && !isExpired
        }

        const shouldDisplay = isExpired || daysRemaining <= displayWindow

        return {
            isLoading,
            isExpired,
            isExpiringSoon,
            expiryTime,
            timeRemaining: timeRemainingFormatted,
            timeRemainingSeconds,
            daysRemaining,
            shouldDisplay,
            displayWindow,
            accessDuration: botData?.accessDuration,
        }
    }, [expiration, botData?.accessDuration, isLoadingExpiration, isLoadingBot])
}
